
import React, { Suspense, useCallback, useMemo } from 'react';
import { pick, prop } from 'ramda';
import { dispatcher } from 'dispatcher';
import { Button } from '@core/js/components/common/Button';
import { SessionTicker } from '@core/js/components/main/SessionTicker';
import { Logo } from '@theme/js/components/main/DefaultHeader/components/Logo/Logo';
import { MyAccount } from '@core/js/components/main/DefaultHeader/components/MyAccount';
import { BalanceBreakdownPopup } from '@theme/js/components/popups/BalanceBreakdownPopup';
import { useAppSelector, useDeviceSelector, usePageSelector, useUserSelector } from '@core/js/store/hooks';
import { useComponentTranslations } from '@core/js/hooks/component/useComponentTranslations';
import { AdditionalUserInfo } from '@core/js/components/main/DefaultHeader/components/AdditionalUserInfo';

import * as hooks from '@core/js/components/main/DefaultHeader/hooks';

import style from './CommonHeader.scss';
import { Icon } from '@core/js/components/common/Icon';
import { getCookie } from 'js/utils/cookie.util';

/*
    There is no reason for portal to have & override DesktopHeader / MobileHeader,
    as they're pretty similar to each other, and may be combined in one component.
    "CommonHeader" kind of merge of both DesktopHeader & MobileHeader.
*/

export const CommonHeader: React.FC = React.memo(() => {
    const translate = useComponentTranslations();
    const onLoginButtonClick = hooks.useOnLoginButtonClick();
    const { showSessionTicker, registrationURL } = useAppSelector(pick([
        'showSessionTicker', 'registrationURL'
    ]));
    const rootClassNames = usePageSelector(prop('rootClassNames'));
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const { overrideMenu, menuWcId } = usePageSelector(pick(['overrideMenu', 'menuWcId']));
    const hideSideMenuIcon = overrideMenu && !menuWcId;
    const openSideMenu = useCallback(() => dispatcher.dispatch('menu:action:open'), []);

    const isSportsbook = useMemo<boolean>(() => {
        const clientType = getCookie('clientType');
        return isLoggedIn && clientType === 'sportsbook' && rootClassNames.includes('fn-custom-color-scheme');
    }, [isLoggedIn, rootClassNames]);

    return (
        <>
            <div className={style.header}>
                <div className={style.info}>
                    {!isDesktop && (
                        <>
                            {!hideSideMenuIcon && (
                                <Icon
                                    type={isSportsbook ? 'sportsbookHeaderHamburger' : 'headerHamburger'}
                                    onClick={openSideMenu}
                                    className={style.menuIcon}
                                />
                            )}
                        </>
                    )}
                    <Logo isSportsbook={isSportsbook} />
                    {isLoggedIn && showSessionTicker && (
                        <Suspense fallback="">
                            <SessionTicker className={style.ticker} />
                        </Suspense>
                    )}
                </div>
                <div className={style.controls}>
                    {isLoggedIn
                        ? (
                            <>
                                {isDesktop && <AdditionalUserInfo />}
                                <BalanceBreakdownPopup />
                                <MyAccount />
                            </>
                        )
                        : (
                            <>
                                <Button
                                    size="small"
                                    color="success"
                                    view="secondary"
                                    className={style.login}
                                    onClick={onLoginButtonClick}
                                >
                                    {translate('login.button.loginHeader')}
                                </Button>
                                {registrationURL && (
                                    <Button
                                        size="small"
                                        color="revert"
                                        view="secondary"
                                        href={registrationURL}
                                        className={style.register}
                                    >
                                        {translate('login.button.join_now')}
                                    </Button>
                                )}
                            </>
                        )
                    }
                </div>
            </div>
            {!isDesktop && <AdditionalUserInfo />}
        </>
    );
});
